import { OwnershipScope } from '@aibees/elephant/types/label';
import {
  filterLabelsByOwnership,
  OwnableLabel,
  ownershipScopeFromTab,
} from '@aibees/elephant/utils/filter-labels-by-ownership';
import { useAuthContext } from '@aibees/shared/auth';
import { PresetVisibility } from '@aibees/shared/definition';
import { useCallback, useMemo } from 'react';

/**
 * Stable `(labels, tabValue) => labels` filter for the All / Mine / Team tabs.
 * Same creator-based rule the In & Exclude list/label filters already use.
 */
export function useFilterLabelsByOwnershipTab() {
  const userId = useAuthContext().user?.id;

  return useCallback(
    <T extends OwnableLabel>(
      labels: T[] | undefined,
      tabValue?: PresetVisibility
    ) =>
      filterLabelsByOwnership(labels, ownershipScopeFromTab(tabValue), userId),
    [userId]
  );
}

/**
 * Tab badge counts shaped for `VisibilityTabs`. Derived from the one array, so
 * Mine + Team === All holds by construction.
 */
export function useOwnershipTabCounts<T extends OwnableLabel>(
  labels: T[] | undefined
) {
  const userId = useAuthContext().user?.id;

  return useMemo(() => {
    const items = labels ?? [];
    const mine = filterLabelsByOwnership(
      items,
      OwnershipScope.MINE,
      userId
    ).length;

    return {
      [PresetVisibility.ALL]: items.length,
      [PresetVisibility.PRIVATE]: mine,
      [PresetVisibility.INTERNAL]: items.length - mine,
    };
  }, [labels, userId]);
}
